import moment from 'moment';

/**
 * Get the start and end date of a date range for a particular calendar mode.
 * @param {string} mode The calendar mode used
 * @param {Date} startDate The start date of the calendar.
 */
export function getCalendarDateRange(mode, startDate) {
    switch (mode) {
        case "day": {
            const date = moment(startDate).startOf("day").toDate();
            return {
                startDate: date,
                endDate: date
            };
        }
        case "week":
            return {
                startDate: startDate ? moment(startDate).toDate() : moment(startDate).startOf("isoWeek").toDate(),
                endDate: startDate ? moment(startDate).add(6, 'days').toDate() : moment(startDate).endOf("isoWeek").toDate()
            };
        case "month":
            return {
                startDate: moment(startDate).startOf("month").toDate(),
                endDate: moment(startDate).endOf("month").toDate()
            };
        default:
            throw new Error(`Invalid calendar mode ${mode}`);
    }
}

/**
 * Enumerate a list of dates in the given range.
 * @param {Date} start The start date to enumerate
 * @param {Date} end The end date to enumerate.
 */
export function enumerateCalendarDates(start, end) {
    const res = [];
    for (let d = start; moment(d).isSameOrBefore(end, 'day'); d = moment(d).add(1, 'day').toDate()) {
        res.push(d);
    }
    return res;
}

/**
 * Partition the given date range into local weeks.
 * @param {Date} start The start date of the range being partitioned
 * @param {Date} end The end date of the range being partitioned
 * @returns {any} The list of weeks partitioned from the date range.
 */
export function partitionDateRangeByWeek(start, end) {
    let weekStart = moment(start).startOf("week");
    const weekScheduleDates = [];
    do {
        const weekEnd = moment(weekStart).endOf("week");
        weekScheduleDates.push({
            startDate: weekStart.toDate(),
            endDate: weekEnd.toDate()
        });
        weekStart = weekStart.add(1, "week");
    }
    while (weekStart.isBefore(end));
    return weekScheduleDates;
}

/**
 * Create a segregated list of dates consisting of lanes of
 * non-intersecting time slots.
 * @param {any} dates The list of dates used for grouping.
 * @param {any} timeSlots The list of time slots to segregate.
 * @param {string} unit The moment unit used to group time slots
 * @param {number?} cutoff The maximum number of lanes available to fill. No limit if null.
 * to a date.
 */
export function segregateTimeSlots(dates, timeSlots, unit, cutoff = null) {
    const lanes = [];
    return dates.map((date, i) => {
        const startTimeSlots = timeSlots.filter(timeSlot =>
            moment(timeSlot.endDateTime).isAfter(date, "unit") // Never include timeslots that have already ended.
            && (moment(timeSlot.startDateTime).isSame(date, unit) // Include the timeslot if it starts on the date
                // or, for the first date in the list, if the timeslot starts before the date.
                || i === 0 && moment(timeSlot.startDateTime).isBefore(date, unit)));
        // Ensure the date lanes has at least as many lanes as the entire date range.
        const dateLanes = lanes.map(() => []);
        for (const timeSlot of startTimeSlots) {
            if (cutoff !== null && dateLanes[cutoff - 1] && dateLanes[cutoff - 1].length) {
                // Stop filling new lanes if the current date has reached the cutoff number of lanes
                break;
            }

            // Pick a time slot where the time slot doesn't intersect with another
            // time slot. If none exists, add a new lane.
            let lane = 0;
            for (lane = 0; lane < lanes.length; lane++) {
                const laneLatestTimeSlot = lanes[lane][lanes[lane].length - 1];
                const isIntersection = moment(laneLatestTimeSlot.startDateTime)
                    .isSameOrBefore(timeSlot.endDateTime, "()", unit)
                    && moment(timeSlot.startDateTime)
                        .isSameOrBefore(laneLatestTimeSlot.endDateTime, "()", unit);
                if (!isIntersection) {
                    lanes[lane].push(timeSlot);
                    dateLanes[lane].push(timeSlot);
                    break;
                }
            }
            if (lane === lanes.length) {
                // Time slot was not inserted into an existing lane, so create a new lane.
                lanes.push([timeSlot]);
                dateLanes.push([timeSlot]);
            }
        }
        return {
            date,
            lanes: dateLanes,
            totalTimeSlots: startTimeSlots.length
        }
    });
}
